<script setup>
import { inject, provide } from "vue";
import { BIconArrowClockwise } from "bootstrap-icons-vue";
import { useRouter } from "vue-router";

import {
  allCategories,
  InteractionPageEnum,
  interactionsCategories,
  softAlertsCategories,
  ticketCategories,
} from "./InboxLeftMenu.utils.js";
import {
  InteractionSortOptionEnum,
  InteractionSourceEnum,
  TicketCategoryStatusEnum,
  ThreadCategoryStatusEnum,
  ThreadStatusEnum,
  InteractionCategoryToThreadStatusMap,
} from "@/utils/constants.js";
import { formatCount } from "@/utils/utils.js";
import IconSort from "@/components/icons/IconSort.vue";
import stores from "@/stores";
import _ from "lodash";
import { useNavigation } from "@/composables/useNavigation";
import { useThreadsContextStore } from "@/stores/threads.js";
import ThreadCard from "./ThreadCard.vue";
import UnknownUserTicketCard from "./UnknownUserTicketCard.vue";
import { useCountsStore } from "@/stores/counts";
import ThreadStatusBadge2 from "./ThreadStatusBadge2.vue";
import InboxThreadFiltersSidebar from "./InboxThreadFiltersSidebar.vue";

const SHOW_ITEMS_OPTIONS = [
  {
    text: "All",
    value: "all",
  },
  {
    text: "Unread only",
    value: "unread_only",
  },
  {
    text: "Read only",
    value: "read_only",
  },
];

const theRouter = useRouter();
const isUnknownUserTicketsRoute = ref(false);
watch(
  () => theRouter.currentRoute.value.path,
  (routePath) => {
    isUnknownUserTicketsRoute.value = routePath === "/inbox/tickets/unknown";
  },
  {
    deep: true,
    immediate: true,
  }
);

const dpts = stores.useDepartmentStore();
const threadsStore = useThreadsContextStore();
const countsStore = useCountsStore();

const inboxFiltersChipsRef = ref(null);
const currentCategory = inject("CURRENT_CATEGORY");

provide("INBOX_CATEGORY", currentCategory);

const {
  queryParams,
  patchQueryParams,
  deleteQueryParam
} =
  useNavigation();

const categoryStatusFilters = computed(() => {
  const category = currentCategory.value;
  if (category === InteractionPageEnum.VA_INTERACTIONS)
    return [ThreadStatusEnum.AI_CLOSED, ThreadStatusEnum.AI_ONGOING]
  else return [];
});

const selectedStatuses = computed({
  get: () => {
    const status = queryParams.value.status || []
    return typeof status === "string" ? [status] : status
  },
  set: (newSelectedStatuses) => patchQueryParams({status: newSelectedStatuses})
});

const updateSelectedStatuses = (status) => {
  if (selectedStatuses.value.includes(status)) {
    selectedStatuses.value = selectedStatuses.value.filter((s) => s !== status);
    return;
  }

  selectedStatuses.value = [...selectedStatuses.value, status];
};

const showFilters = computed(
  () => !(isUnknownUserTicketsRoute.value)
);

const showInteractionFilters = computed(
  () => Object.keys(interactionsCategories).includes(currentCategory.value)
);
const showTicketFilters = computed(
  () => Object.keys(ticketCategories).includes(currentCategory.value)
);
const showSoftAlertFilters = computed(
  () => (
    Object.keys(softAlertsCategories).includes(currentCategory.value) ||
      currentCategory.value === InteractionPageEnum.ALL_INTERACTIONS
  )
);
const showInteractionTicketFilters = computed(
  () => currentCategory.value === InteractionPageEnum.ALL_INTERACTIONS
);

const showAllEngagementFilters = computed(
  () => currentCategory.value === InteractionPageEnum.ALL_INTERACTIONS
);

const newTicketsDropdownOptions = [
  ThreadStatusEnum.NEW,
  ThreadStatusEnum.REOPENED,
  ThreadStatusEnum.ESCALATED,
  ThreadStatusEnum.PENDING_FORM_SUBMISSION,
].map(status => ({
  text: status,
  value: status,
}));

const ongoingTicketsDropdownOptions = [
  ThreadStatusEnum.OPEN,
  ThreadStatusEnum.PENDING_USER_REPLY,
].map(status => ({
  text: (
    status === ThreadStatusEnum.PENDING_USER_REPLY
      ? "Pending User Reply"
      : status
  ),
  value: status,
}));

const closedTicketsDropdownOptions = [
  ThreadStatusEnum.SUPPORT_CLOSED,
  ThreadStatusEnum.USER_CLOSED,
  ThreadStatusEnum.AUTO_CLOSED,
].map(status => ({
  text: status,
  value: status,
}));

const otherDropdownOptions = [
ThreadStatusEnum.ONHOLD
].map(status => ({
  text: status,
  value: status,
}));

const softAlertDropdownOptions = [
  ThreadStatusEnum.SOFT_ALERT,
].map(status => ({
  text: status,
  value: status,
}));

const interactionDropdownOptions = [
  ThreadStatusEnum.AI_CLOSED,
  ThreadStatusEnum.AI_ONGOING,
].map(status => ({
  text: status,
  value: status,
}));

const interactionTicketsDropdownOptions = [
  ThreadCategoryStatusEnum.TICKETS
].map(status => ({
  text: status,
  value: status,
}));


// const interactionDropdownOptions = computed(() => {
//   const aiInteractionStatusOptions = [ThreadCategoryStatusEnum.AI_ONGOING, ThreadCategoryStatusEnum.AI_CLOSED]
//   const statuses = currentCategory.value === InteractionPageEnum.VA_INTERACTIONS ? aiInteractionStatusOptions : Object.values(ThreadCategoryStatusEnum)

//   return statuses.map(status => ({
//     text: status,
//     value: status,
//   }))
// })



const selectedThreadIds = ref([]);
const selectedDepartments = computed({
  get: () => {
    const department = queryParams.value.department || []
    return typeof department === "string" ? [department] : department
  },
  set: (newDepartments) => patchQueryParams({ department: newDepartments })
});

const selectedSortOption = computed({
  get: () => queryParams.value.sort_option || null,
  set: (newSortOption) => {
    if(!newSortOption) {
      deleteQueryParam("sort_option")
      return
    }

    patchQueryParams({ sort_option: newSortOption})
  }
});

const currentPage = computed({
  get: () => parseInt(queryParams.value.page, 10),
  set: (newPage) => patchQueryParams({ page: newPage }),
});


// updates an array of threadIds that have been selected
const updateSelectedThreadIds = (threadId) => {
  const updatedThreadIds = (
    selectedThreadIds.value.includes(threadId)
      ? selectedThreadIds.value.filter((id) => id !== threadId)
      : [...selectedThreadIds.value, threadId]
  );

  console.log("updatedThreadIds: ", updatedThreadIds);
  selectedThreadIds.value = updatedThreadIds;
};

const sortOptions = {
  [InteractionSortOptionEnum.MOST_RECENT]: {
    text: "Most recent activity",
    value: InteractionSortOptionEnum.MOST_RECENT,
  },
  [InteractionSortOptionEnum.CREATED_TIME_NEWEST_TO_OLDEST]: {
    text: "Created time: Newest to oldest",
    value: InteractionSortOptionEnum.CREATED_TIME_NEWEST_TO_OLDEST,
  },
  [InteractionSortOptionEnum.CREATED_TIME_OLDEST_TO_NEWEST]: {
    text: "Created time: Oldest to newest",
    value: InteractionSortOptionEnum.CREATED_TIME_OLDEST_TO_NEWEST,
  },
};

const selectedSortOptionText = computed(
  () => sortOptions[selectedSortOption.value]
);

const currentSelectedDepartmentsCount = computed(() => {
  return selectedDepartments.value.length;
});

const handleBatchUnreadByPatch = async (markAsRead) => {
  const updateArr = selectedThreadIds.value.map((id) =>
    threadsStore.patchSeenBy(id, markAsRead)
  );
  await Promise.all(updateArr);

  // clear state
  selectedThreadIds.value = [];
};

// have to build it like this bc showing items component...
const toggleSelectAll = () => {
  // select all
  if (selectedThreadIds.value.length < threadsStore.sortedThreads.length) {
    const allThreadIds = threadsStore.sortedThreads.map((t) => t._id);
    selectedThreadIds.value = allThreadIds;
  } else {
    selectedThreadIds.value = [];
  }
};

const refreshInboxData = async () => {
  await threadsStore.fetchThreads({
    ...queryParams.value,
    _inbox_category: currentCategory.value,
  });
  await countsStore.fetchInteractionCounts();
};

const currentTotalCount = computed(() => {
  const currentCategoryValue = allCategories[currentCategory.value];
  const countKey = currentCategoryValue?.countKey;
  if (!countKey) return 0;

  return countsStore.interactionCounts[countKey]?.total_count ?? 0;
});

const currentThreadStatusCountMap = computed(() => {
  const currentCategoryValue = allCategories[currentCategory.value];
  const countKey = currentCategoryValue?.countKey;
  if (!countKey) return {};

  return countsStore.interactionCounts[countKey]?.thread_status_count_map ?? {};
});

// make the total count based off the threads status map
const categoryTotalCount = computed(() => {

  // status Arr is based on the category
  const categoryToStatusMap = InteractionCategoryToThreadStatusMap[currentCategory.value] || {}
  const statusArr = Object.values(categoryToStatusMap)

  const totalCount = statusArr.reduce((acc, cur) => {
    const statusCount = currentThreadStatusCountMap.value[cur] ?? 0
    return acc + statusCount
  }, 0)

  return totalCount

});

const getThreadStatusCount = (status) => {
  const map = currentThreadStatusCountMap.value;
  return map?.[status] ?? 0;
};

const showUnknownUserTicketModal = ref(false);
const thisUnknownUserTicket = ref({});
const onShowUnknownUserTicketModal = (threadId) => {
  const ticket = threadsStore.sortedThreads.find(
    (thread) => thread._id == threadId
  );
  if (!ticket) {
    console.error("Cannot find Unknown User Ticket", threadId);
    return;
  }
  console.log("Unknown User Ticket", ticket);
  thisUnknownUserTicket.value = ticket;
  showUnknownUserTicketModal.value = true;
};
const onCloseUnknownUserTicketModal = () => {
  showUnknownUserTicketModal.value = false;
  thisUnknownUserTicket.value = {};
};
const onRemoveUnknownUserTicket = async (value) => {
  await refreshInboxData();
};

const onlyUnknownUserTickets = computed(() => {
  return threadsStore.sortedThreads.filter(
    (thread) => thread.source === InteractionSourceEnum.UNKNOWN_USER_TICKET
  );
});

const isFiltersMenuVisible = ref(false);
const toggleFiltersMenu = () => {
  isFiltersMenuVisible.value = !isFiltersMenuVisible.value;
};

const countOfAppliedFilters = ref(0);
const updateActiveFiltersCount = (count) => {
  countOfAppliedFilters.value = count;
};
</script>

<template>
  <div class="header">
    <h5 class="mb-0 text-neutral-600">
      <span>
        {{ allCategories[currentCategory].text }}
      </span>
      <!-- <span v-if="categoryTotalCount">
        ({{ formatCount(categoryTotalCount) }})
      </span> -->
    </h5>

    <div class="d-flex justify-content-between my-mt-4">
      <div
        v-if="showFilters"
        class="column-gap-2 d-flex justify-content-start align-items-center bg-white"
      >
        <div v-if="showTicketFilters" class="d-flex gap-2">
          <StatusFilterDropdown
            class="custom-dropdown-options"
            auto-close="outside"
            header="Filter By New Tickets"
            padding-size="big"
            label="New Tickets"
            v-model="selectedStatuses"
            :options="newTicketsDropdownOptions"
            :show-search="false"
            :sort-options="false"
            theme="red"
            :counts="currentThreadStatusCountMap"
            multiple
          />
          <StatusFilterDropdown
            class="custom-dropdown-options"
            auto-close="outside"
            header="Filter By Ongoing Tickets"
            padding-size="big"
            label="Ongoing Tickets"
            v-model="selectedStatuses"
            :options="ongoingTicketsDropdownOptions"
            :show-search="false"
            :sort-options="false"
            theme="yellow"
            :counts="currentThreadStatusCountMap"
            multiple
          />
          <StatusFilterDropdown
            class="custom-dropdown-options"
            auto-close="outside"
            header="Filter By Closed Tickets"
            padding-size="big"
            label="Closed Tickets"
            v-model="selectedStatuses"
            :options="closedTicketsDropdownOptions"
            :show-search="false"
            :sort-options="false"
            theme="green"
            :counts="currentThreadStatusCountMap"
            multiple
          />
          <StatusFilterDropdown
            class="custom-dropdown-options"
            auto-close="outside"
            header="Filter By Other"
            padding-size="big"
            label="Other"
            v-model="selectedStatuses"
            :options="otherDropdownOptions"
            :show-search="false"
            :sort-options="false"
            theme="gray"
            :counts="currentThreadStatusCountMap"
            multiple
          />
        </div>
        <ThreadStatusBadge2
          v-for="status in categoryStatusFilters"
          :key="status"
          :value="status"
          @toggle="updateSelectedStatuses"
          :selected="selectedStatuses.includes(status)"
          :count="getThreadStatusCount(status)"
          padding-size="big"
        />
        <StatusFilterDropdown
          v-if="showAllEngagementFilters"
          class="custom-dropdown-options"
          auto-close="outside"
          header="Filter By AI"
          padding-size="big"
          label="AI"
          v-model="selectedStatuses"
          :options="interactionDropdownOptions"
          :show-search="false"
          :sort-options="false"
          theme="green"
          :counts="currentThreadStatusCountMap"
          multiple
        />
        <StatusFilterDropdown
          v-if="showSoftAlertFilters"
          class="custom-dropdown-options"
          auto-close="outside"
          header="Filter By Sort Alerts"
          padding-size="big"
          label="Sort Alerts"
          v-model="selectedStatuses"
          :options="softAlertDropdownOptions"
          :show-search="false"
          :sort-options="false"
          theme="yellow"
          :counts="currentThreadStatusCountMap"
          multiple
        />
        <StatusFilterDropdown
          v-if="showInteractionTicketFilters"
          class="custom-dropdown-options"
          auto-close="outside"
          header="Filter By Tickets"
          padding-size="big"
          label="Tickets"
          v-model="selectedStatuses"
          :options="interactionTicketsDropdownOptions"
          :show-search="false"
          :sort-options="false"
          theme="red"
          :counts="currentThreadStatusCountMap"
          multiple
        />
      </div>
      <!-- fill up the space -->
      <div v-else />

      <div class="d-flex column-gap-2 align-items-center">
        <dropdown
          class="custom-dropdown-options"
          auto-close="outside"
          v-if="currentCategory !== InteractionPageEnum.MY_DEPARTMENTS"
          header="Filter by Department"
          padding-size="big"
          label="Department"
          :current-selected-option="currentSelectedDepartmentsCount"
          :options="dpts.dropdownOptions"
          v-model="selectedDepartments"
          multiple
        >
        </dropdown>

        <dropdown
          v-model="selectedSortOption"
          header="Sort"
          class="custom-sort__dropdown"
          padding-size="big"
          label="Sort"
          :displaySelectedOption="false"
          :current-selected-option="selectedSortOptionText"
          :options="Object.values(sortOptions)"
          :show-search="false"
        >
          <template #pre-label-icon>
            <IconSort />
          </template>
        </dropdown>

        <div
            class="status-pill d-flex column-gap-2 align-items-center text-neutral-400 cursor-pointer"
            @click="toggleFiltersMenu"
          >
            <IconFilter />
            <span class="font-size-xs">
              Filters
              <span v-if="countOfAppliedFilters > 0">
                ({{ countOfAppliedFilters }})
              </span>
            </span>
          </div>
        <BIconArrowClockwise
          class="cursor-pointer"
          @click.stop="refreshInboxData"
        />
      </div>
    </div>
  </div>

  <list-loader :show="threadsStore.isLoading" />
  <InboxFiltersChips
    ref="inboxFiltersChipsRef"
    style="padding: 2rem 4rem 0rem 3rem"
    @deleteFilter="deleteQueryParam"
  />
  <div
    v-if="
      !threadsStore.isLoading &&
      threadsStore.isSuccess &&
      threadsStore.sortedThreads.length
    "
    :class="[
      'content d-flex flex-column flex-grow-1',
      { isFilterChipsVisible: false },
    ]"
  >
    <div
      v-if="!isUnknownUserTicketsRoute"
      class="showing-items-top d-flex align-items-center justify-content-between"
    >
      <showing-items
        :per-page="queryParams.limit"
        :selected-count="selectedThreadIds.length"
        :total-items="threadsStore.totalCount"
        :options="SHOW_ITEMS_OPTIONS"
        showCheckbox
        @select-all-on-page="toggleSelectAll"
      />
      <div v-if="selectedThreadIds?.length" class="d-flex gap-2">
        <div
          @click.stop="handleBatchUnreadByPatch(true)"
          class="mark-as-read-container cursor-pointer d-flex align-items-center justify-content-between"
        >
          <IconRead />
          <div class="ms-2 text-neutral-400 font-size-xs">
            Mark as Read ({{ selectedThreadIds.length }})
          </div>
        </div>
        <div
          @click.stop="handleBatchUnreadByPatch(false)"
          class="mark-as-read-container cursor-pointer d-flex align-items-center justify-content-between"
        >
          <IconRead />
          <div class="ms-2 text-neutral-400 font-size-xs">
            Mark as Unread ({{ selectedThreadIds.length }})
          </div>
        </div>
      </div>
    </div>

    <div
      class="position-relative mt-4 overflow-auto flex-grow-1"
    >
      <div
        v-if="isUnknownUserTicketsRoute"
      >
        <UnknownUserTicketCard
          v-for="thread in onlyUnknownUserTickets"
          :key="thread._id"
          :threadDetails="thread"
          @showUnknownUserTicketModal="onShowUnknownUserTicketModal"
        />
      </div>
      <div
        v-else
      >
        <ThreadCard
          v-for="thread in threadsStore.sortedThreads"
          :key="thread._id"
          :threadDetails="thread"
          :selectedThreadIds="selectedThreadIds"
          @toggle-checked="updateSelectedThreadIds"
        />
      </div>
    </div>

    <div class="mt-4 d-flex align-items-center justify-content-between">
      <showing-items
        :per-page="queryParams.limit"
        :selected-count="selectedThreadIds.length"
        :total-items="threadsStore.totalCount"
      />
      <pagination-buttons
        v-if="threadsStore.totalCount > queryParams.limit"
        :total-rows="threadsStore.totalCount"
        :per-page="queryParams.limit"
        v-model="currentPage"
      />
    </div>
  </div>

  <all-caught-up
    v-else-if="!threadsStore.isLoading && threadsStore.isSuccess"
  />

  <InboxThreadFiltersSidebar
    :show-ticket-owner="showTicketOwnerFilter"
    :is-filters-menu-visible="isFiltersMenuVisible"
    @toggle-filters-menu-visibility="toggleFiltersMenu"
    @update-active-filters-count="updateActiveFiltersCount"
  />

  <UnknownUserTicketModal
    v-if="isUnknownUserTicketsRoute && showUnknownUserTicketModal"
    v-model="showUnknownUserTicketModal"
    @close="onCloseUnknownUserTicketModal"
    @removeTicket="onRemoveUnknownUserTicket"
    :threadDetails="thisUnknownUserTicket"
  />
</template>

<style scoped lang="scss">
$--header-height: 190px;
$--filter-chips-height: 100px;

.sidebar {
  max-width: 15rem;
}

.header {
  padding: 3rem 4rem 2rem 3rem;
  border-bottom: 1px solid $neutral-150;
}

.inbox {
  background-color: $neutral-500;
  padding: 3rem 1.5rem;
}

.content {
  padding: 2rem 4rem 1rem 3rem;
  overflow: auto;
  @include calcHeight($--header-height);
  @include calcWidth(320px);

  &.isFilterChipsVisible {
    @include calcHeight($--header-height, $--filter-chips-height);
  }

  .showing-items-top {
    min-height: 36px;
  }
}

.mark-as-read-container {
  padding: 8px 12px;
  border-radius: 20px;
  border: 1px solid $neutral-150;
  &:active {
    background: $neutral-100;
    border: 1px solid $neutral-200;
  }
  &:hover {
    background: $neutral-50;
    border: 1px solid $neutral-200;
  }
}

.circle-icon {
  font-size: 10px;
  fill: var(--primary-250);
}

.status-pill {
  padding: 0.5rem 0.75rem;
  border: 1px solid $bluish-gray-300;
  border-radius: 1.25rem;
  font-size: 0.875rem;

  svg {
    width: 12px;
    height: 12px;
  }
}

.custom-dropdown-options {
  :deep(.dropdown-menu) {
    padding: 0;
  }

  .dropdown-item {
    padding: 12px;
    color: $neutral-400;
    border-radius: 0.625rem;
    font-size: $font-size-sm;

    &.active {
      background: var(--primary-0);
      border: 1px solid var(--primary-50);
    }

    &:focus {
      background: var(--primary-0);
    }

    &:active {
      background: white;
    }
  }
}

.custom-active {
  background: var(--primary-0);
  border: 1px solid var(--primary-50);

  .icon-check {
    width: 24px;
    height: 24px;
    fill: var(--primary-250);
  }
}

.custom-sort__dropdown {
  :deep .dropdown-menu {
    width: 280px;
  }
}
</style>
